import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, ElementRef, ChangeDetectorRef, Component, Input, Output, HostBinding, NgZone, ChangeDetectionStrategy, ContentChildren, ViewChild, ViewChildren, HostListener, NgModule } from '@angular/core';
import * as i2 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';
import { __decorate } from 'tslib';
import { SiUIStateService, isRTL, WebComponentContentChildren } from '@simpl/element-ng/common';
import { Subject, of, merge, fromEvent } from 'rxjs';
import { startWith, delay, tap, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';

class SiSplitPartComponent {
    _arrows = {
        h: ['arrow-start', 'arrow-end'],
        v: ['arrow-up', 'arrow-down']
    };
    _size;
    actions = [];
    collapseDirection = 'start';
    /**
     * Sets the icon class that is used in the buttons of split parts to
     * collapse and uncollapse the parts.
     */
    collapseIconClass = 'element-double-right';
    /**
     * Collapse only to the given min size.
     */
    collapseToMinSize = false;
    /**
     * Sets the status color on the split part header, visible as a bottom border and,
     * if a headerStatusIcon is defined, as the icon´s background color.
     */
    headerStatusColor;
    /**
     * Sets the icon class that is used as status icon in the split part header.
     */
    headerStatusIconClass;
    headerTemplate;
    /**
     * Sets the title of the split part header.
     */
    heading;
    /**
     * Minimum size in px.
     */
    minSize = 0;
    /**
     * When a split part is collapsed, the content gets hidden but it will
     * still remain within the DOM. If you want to remove and destroy the component
     * in collapsed mode and recreate it on un-collapse, set this property to
     * true.
     */
    removeContentOnCollapse = false;
    /**
     * Defines the behavior of the split part during scaling.
     * E.g. when set to `none`, the spit part will keep its current size even when the parent container grows or shrinks.
     */
    scale = 'auto';
    /**
     * Defines if the collapse button of a split part is displayed. Default value is true.
     */
    showCollapseButton = true;
    /**
     * Defines if the header of the split part is visible. Default is true.
     */
    showHeader = true;
    /**
     * Aria label for collapse button. Needed for a11y
     */
    collapseLabel = 'collapse';
    /**
     * An optional stateId to uniquely identify a component instance.
     * Required for persistence of ui state.
     */
    stateId;
    collapseChanged = new EventEmitter();
    stateChange = new EventEmitter();
    collapsed = false;
    /** @internal */
    orientation = 'vertical';
    headerContext;
    trackByIndex = (index) => index;
    _elRef = inject((ElementRef));
    cdRef = inject(ChangeDetectorRef);
    constructor() {
        this.headerContext = {
            $implicit: this
        };
    }
    /** @default `true` */
    set expanded(value) {
        if (this.expanded !== value) {
            this.toggleCollapse();
        }
    }
    get expanded() {
        return !this.collapsed;
    }
    /**
     * Header size in px.
     * @internal
     */
    get headerSize() {
        const header = this._elRef.nativeElement.querySelector('.si-split-part-header');
        return header
            ? this.orientation === 'horizontal' && this.collapsed
                ? header.clientWidth
                : header.clientHeight
            : 0;
    }
    /**
     * Expanded size in px.
     */
    set size(value) {
        if (this._size !== value) {
            this._size = value;
            this.stateChange.emit(this.state);
        }
    }
    get size() {
        return this._size;
    }
    get statusContainerStyle() {
        const header = this._elRef.nativeElement.querySelector('.si-split-part-header');
        if (header) {
            // we need the header´s padding values to adjust the position of the status icon container
            const headerStyle = window.getComputedStyle(header);
            return {
                'block-size': `calc(100% + ${headerStyle.paddingTop} + ${headerStyle.paddingBottom}`,
                'margin-block-end': `-${headerStyle.paddingBottom}`,
                'margin-inline-start': `-${headerStyle.paddingInlineStart}`,
                'margin-inline-end': headerStyle.paddingInlineEnd,
                'margin-block-start': `-${headerStyle.paddingTop}`
            };
        }
        return null;
    }
    /** @internal */
    applyStyle(style) {
        Object.assign(this._elRef.nativeElement.style, style);
    }
    getCollapseArrow() {
        let tempArrows = this._arrows.h;
        if (this.orientation === 'vertical') {
            tempArrows = this._arrows.v;
        }
        let arrowIndex = 0;
        if (this.collapseDirection === 'end') {
            arrowIndex = 1 - arrowIndex;
        }
        if (this.collapsed) {
            arrowIndex = 1 - arrowIndex;
        }
        return tempArrows[arrowIndex];
    }
    /** @internal */
    getHeight() {
        return this._elRef.nativeElement.offsetHeight;
    }
    /** @internal */
    getWidth() {
        return this._elRef.nativeElement.offsetWidth;
    }
    /** @internal */
    initPart() {
        if (typeof this.minSize === 'number') {
            if (this.orientation === 'horizontal') {
                this.applyStyle({
                    'min-width': `${this.minSize}px`,
                    'min-height': 'auto'
                });
            }
            else {
                this.applyStyle({
                    'min-width': 'auto',
                    'min-height': `${this.minSize}px`
                });
            }
        }
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    /**
     * Returns true if this split part is horizontal by comparing the orientation property
     * with 'horizontal'.
     * @internal
     */
    isHorizontal() {
        return this.orientation === 'horizontal';
    }
    /**
     * Toggles the collapsed or expanded state.
     */
    toggleCollapse() {
        this.collapsed = !this.collapsed;
        this.collapseChanged.emit(this.collapsed);
        this.stateChange.emit(this.state);
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    /** @internal */
    updateSize() {
        if (this.expanded && this.scale === 'none') {
            this.size = this.isHorizontal() ? this.getWidth() : this.getHeight();
            this.cdRef.detectChanges();
            this.cdRef.markForCheck();
        }
    }
    get state() {
        return { expanded: this.expanded, size: this.size };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitPartComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSplitPartComponent, selector: "si-split-part", inputs: { actions: "actions", collapseDirection: "collapseDirection", collapseIconClass: "collapseIconClass", collapseToMinSize: "collapseToMinSize", headerStatusColor: "headerStatusColor", headerStatusIconClass: "headerStatusIconClass", headerTemplate: "headerTemplate", heading: "heading", minSize: "minSize", removeContentOnCollapse: "removeContentOnCollapse", scale: "scale", showCollapseButton: "showCollapseButton", showHeader: "showHeader", collapseLabel: "collapseLabel", stateId: "stateId", expanded: "expanded", size: "size" }, outputs: { collapseChanged: "collapseChanged", stateChange: "stateChange" }, host: { properties: { "class.is-collapsed": "this.collapsed" } }, ngImport: i0, template: "<div\n  *ngIf=\"!headerTemplate && showHeader\"\n  class=\"si-split-part-header\"\n  [class.is-collapsed]=\"collapsed\"\n  [class.is-horizontal]=\"orientation === 'horizontal'\"\n  [class.has-status-color]=\"headerStatusColor && !collapsed\"\n  [style.color]=\"headerStatusColor && !collapsed ? headerStatusColor : null\"\n>\n  <div\n    *ngIf=\"headerStatusIconClass && !collapsed\"\n    class=\"si-split-part-status-container\"\n    [ngStyle]=\"statusContainerStyle\"\n  >\n    <i class=\"si-split-part-status\" [ngClass]=\"headerStatusIconClass\"></i>\n  </div>\n  <div class=\"si-split-part-title\">\n    <span class=\"text-truncate\">{{ heading | translate }}</span>\n  </div>\n  <div *ngIf=\"!collapsed\" class=\"si-split-part-actions\">\n    <button\n      *ngFor=\"let action of actions; trackBy: trackByIndex\"\n      type=\"button\"\n      class=\"si-split-button\"\n      [title]=\"action.tooltip || ''\"\n      [attr.aria-label]=\"action.tooltip\"\n      (click)=\"action.click($event)\"\n    >\n      <i [ngClass]=\"action.iconClass\"></i>\n    </button>\n  </div>\n  <div *ngIf=\"showCollapseButton\" class=\"si-split-part-collapse-button\">\n    <button\n      type=\"button\"\n      class=\"si-split-button\"\n      [attr.aria-label]=\"collapseLabel | translate\"\n      [class.flip-rtl]=\"orientation === 'horizontal'\"\n      (click)=\"toggleCollapse()\"\n    >\n      <i [ngClass]=\"[collapseIconClass, getCollapseArrow()]\"></i>\n    </button>\n  </div>\n</div>\n\n<ng-container *ngIf=\"headerTemplate\">\n  <ng-container *ngTemplateOutlet=\"headerTemplate; context: headerContext\"></ng-container>\n</ng-container>\n\n<div\n  class=\"si-split-part-content\"\n  [class.hidden]=\"!removeContentOnCollapse && collapsed && !collapseToMinSize\"\n>\n  <ng-content *ngIf=\"!removeContentOnCollapse || !collapsed\"></ng-content>\n</div>\n", styles: [":host{display:flex;flex-grow:1;flex-shrink:1;flex-basis:var(--si-split-part-flex-basis);flex-direction:column;overflow:hidden}:host.is-collapsed{min-inline-size:auto!important;min-block-size:auto!important}:host ::ng-deep si-split{flex:1}.si-split-part-content{flex-basis:0px;flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow:hidden;inline-size:100%}.si-split-part-header{display:flex;flex-direction:row;flex:none;align-items:center;padding:var(--si-split-part-header-padding);background:var(--si-split-part-header-background);block-size:var(--si-split-part-header-height)}.si-split-part-header.is-collapsed{border-block-end-width:var(--si-split-part-header-border-width);border-style:var(--si-split-part-header-border-style);border-color:var(--si-split-part-header-border-color)}.si-split-part-header.has-status-color{box-shadow:inset 0 calc(-1 * var(--si-split-part-header-status-border-width)) 0 0}.si-split-part-header.has-status-color .si-split-part-status-container{background-color:currentColor}.si-split-part-header.is-collapsed.is-horizontal{flex:auto;flex-direction:column;max-block-size:100%;padding:var(--si-split-part-header-collapsed-horizontal-padding)}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title{inline-size:100%}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title>span{writing-mode:vertical-rl;transform:rotate(180deg);margin:0}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-collapse-button{order:-1}.si-split-part-status-container{display:inline-flex;inline-size:var(--si-split-part-header-height);max-inline-size:var(--si-split-part-header-height);padding:var(--si-split-part-header-status-container-padding)}.si-split-part-status-container .si-split-part-status{color:var(--si-split-part-header-status-icon-color);font-size:var(--si-split-part-header-status-icon-size);margin:auto}.si-split-part-title{display:flex;justify-content:center;flex:auto;font-weight:var(--si-split-part-title-font-weight);font-size:var(--si-split-part-title-font-size);color:var(--si-split-part-title-color);line-height:var(--si-split-part-title-line-height);overflow:hidden}.si-split-part-title>span{display:block;word-wrap:normal;line-height:initial;min-inline-size:0;max-inline-size:100%;margin-inline-end:auto}.si-split-part-actions{display:flex;flex:none;align-items:center;color:initial;margin-inline:var(--si-split-part-action-margin)}.si-split-button{display:inline-block;padding:var(--si-split-button-padding);color:var(--si-split-button-color);overflow:hidden;white-space:nowrap;text-overflow:var(--si-split-button-text-overflow);text-decoration:none;font-size:var(--si-split-button-font-size);line-height:var(--si-split-button-line-height);vertical-align:middle;outline:none;-webkit-user-select:none;user-select:none;background:none;border:0;cursor:pointer}.si-split-button:hover{background:var(--si-split-button-shade-background-hover)}.si-split-button.has-noflex{flex:none}.si-split-button .arrow-start{transform:rotate(180deg)}.si-split-button .arrow-down{transform:rotate(90deg)}.si-split-button .arrow-up{transform:rotate(270deg)}.hidden{display:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i2.SiTranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitPartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-split-part', template: "<div\n  *ngIf=\"!headerTemplate && showHeader\"\n  class=\"si-split-part-header\"\n  [class.is-collapsed]=\"collapsed\"\n  [class.is-horizontal]=\"orientation === 'horizontal'\"\n  [class.has-status-color]=\"headerStatusColor && !collapsed\"\n  [style.color]=\"headerStatusColor && !collapsed ? headerStatusColor : null\"\n>\n  <div\n    *ngIf=\"headerStatusIconClass && !collapsed\"\n    class=\"si-split-part-status-container\"\n    [ngStyle]=\"statusContainerStyle\"\n  >\n    <i class=\"si-split-part-status\" [ngClass]=\"headerStatusIconClass\"></i>\n  </div>\n  <div class=\"si-split-part-title\">\n    <span class=\"text-truncate\">{{ heading | translate }}</span>\n  </div>\n  <div *ngIf=\"!collapsed\" class=\"si-split-part-actions\">\n    <button\n      *ngFor=\"let action of actions; trackBy: trackByIndex\"\n      type=\"button\"\n      class=\"si-split-button\"\n      [title]=\"action.tooltip || ''\"\n      [attr.aria-label]=\"action.tooltip\"\n      (click)=\"action.click($event)\"\n    >\n      <i [ngClass]=\"action.iconClass\"></i>\n    </button>\n  </div>\n  <div *ngIf=\"showCollapseButton\" class=\"si-split-part-collapse-button\">\n    <button\n      type=\"button\"\n      class=\"si-split-button\"\n      [attr.aria-label]=\"collapseLabel | translate\"\n      [class.flip-rtl]=\"orientation === 'horizontal'\"\n      (click)=\"toggleCollapse()\"\n    >\n      <i [ngClass]=\"[collapseIconClass, getCollapseArrow()]\"></i>\n    </button>\n  </div>\n</div>\n\n<ng-container *ngIf=\"headerTemplate\">\n  <ng-container *ngTemplateOutlet=\"headerTemplate; context: headerContext\"></ng-container>\n</ng-container>\n\n<div\n  class=\"si-split-part-content\"\n  [class.hidden]=\"!removeContentOnCollapse && collapsed && !collapseToMinSize\"\n>\n  <ng-content *ngIf=\"!removeContentOnCollapse || !collapsed\"></ng-content>\n</div>\n", styles: [":host{display:flex;flex-grow:1;flex-shrink:1;flex-basis:var(--si-split-part-flex-basis);flex-direction:column;overflow:hidden}:host.is-collapsed{min-inline-size:auto!important;min-block-size:auto!important}:host ::ng-deep si-split{flex:1}.si-split-part-content{flex-basis:0px;flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow:hidden;inline-size:100%}.si-split-part-header{display:flex;flex-direction:row;flex:none;align-items:center;padding:var(--si-split-part-header-padding);background:var(--si-split-part-header-background);block-size:var(--si-split-part-header-height)}.si-split-part-header.is-collapsed{border-block-end-width:var(--si-split-part-header-border-width);border-style:var(--si-split-part-header-border-style);border-color:var(--si-split-part-header-border-color)}.si-split-part-header.has-status-color{box-shadow:inset 0 calc(-1 * var(--si-split-part-header-status-border-width)) 0 0}.si-split-part-header.has-status-color .si-split-part-status-container{background-color:currentColor}.si-split-part-header.is-collapsed.is-horizontal{flex:auto;flex-direction:column;max-block-size:100%;padding:var(--si-split-part-header-collapsed-horizontal-padding)}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title{inline-size:100%}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title>span{writing-mode:vertical-rl;transform:rotate(180deg);margin:0}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-collapse-button{order:-1}.si-split-part-status-container{display:inline-flex;inline-size:var(--si-split-part-header-height);max-inline-size:var(--si-split-part-header-height);padding:var(--si-split-part-header-status-container-padding)}.si-split-part-status-container .si-split-part-status{color:var(--si-split-part-header-status-icon-color);font-size:var(--si-split-part-header-status-icon-size);margin:auto}.si-split-part-title{display:flex;justify-content:center;flex:auto;font-weight:var(--si-split-part-title-font-weight);font-size:var(--si-split-part-title-font-size);color:var(--si-split-part-title-color);line-height:var(--si-split-part-title-line-height);overflow:hidden}.si-split-part-title>span{display:block;word-wrap:normal;line-height:initial;min-inline-size:0;max-inline-size:100%;margin-inline-end:auto}.si-split-part-actions{display:flex;flex:none;align-items:center;color:initial;margin-inline:var(--si-split-part-action-margin)}.si-split-button{display:inline-block;padding:var(--si-split-button-padding);color:var(--si-split-button-color);overflow:hidden;white-space:nowrap;text-overflow:var(--si-split-button-text-overflow);text-decoration:none;font-size:var(--si-split-button-font-size);line-height:var(--si-split-button-line-height);vertical-align:middle;outline:none;-webkit-user-select:none;user-select:none;background:none;border:0;cursor:pointer}.si-split-button:hover{background:var(--si-split-button-shade-background-hover)}.si-split-button.has-noflex{flex:none}.si-split-button .arrow-start{transform:rotate(180deg)}.si-split-button .arrow-down{transform:rotate(90deg)}.si-split-button .arrow-up{transform:rotate(270deg)}.hidden{display:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { actions: [{
                type: Input
            }], collapseDirection: [{
                type: Input
            }], collapseIconClass: [{
                type: Input
            }], collapseToMinSize: [{
                type: Input
            }], headerStatusColor: [{
                type: Input
            }], headerStatusIconClass: [{
                type: Input
            }], headerTemplate: [{
                type: Input
            }], heading: [{
                type: Input
            }], minSize: [{
                type: Input
            }], removeContentOnCollapse: [{
                type: Input
            }], scale: [{
                type: Input
            }], showCollapseButton: [{
                type: Input
            }], showHeader: [{
                type: Input
            }], collapseLabel: [{
                type: Input
            }], stateId: [{
                type: Input
            }], collapseChanged: [{
                type: Output
            }], stateChange: [{
                type: Output
            }], collapsed: [{
                type: HostBinding,
                args: ['class.is-collapsed']
            }], expanded: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

const COLLAPSED_SIZE = 'none';
class SplitPart {
    component;
    _index;
    _headerSize;
    _minSize;
    _size;
    constructor(component, _index) {
        this.component = component;
        this._index = _index;
        this._minSize = 0;
        this._size = 0;
    }
    get actualSize() {
        return this.expanded ? this.size : this.collapseToMinSize ? this.minSize : this._headerSize;
    }
    get direction() {
        return this.component.collapseDirection;
    }
    get expanded() {
        return this.component.expanded;
    }
    get hasHeader() {
        return this.component.showHeader;
    }
    get index() {
        return this._index;
    }
    get minSize() {
        return this._minSize;
    }
    get pixelSize() {
        return this.component.isHorizontal() ? this.component.getWidth() : this.component.getHeight();
    }
    get scale() {
        return this.component.scale;
    }
    get size() {
        return this._size;
    }
    set size(value) {
        this._size = !isNaN(value) ? value : 0;
    }
    get stateId() {
        return this.component.stateId;
    }
    applyStyle() {
        this.component.applyStyle(this.style);
    }
    collapsed() {
        this.component.collapsed = true;
    }
    notifySizeChange() {
        this.component.updateSize();
    }
    observeStateChange(orientation) {
        this.updateOrientation(orientation);
        return this.component.collapseChanged;
    }
    updateOrientation(orientation) {
        this.component.orientation = orientation;
        this.component.initPart();
        this.component.applyStyle(this.style);
    }
    updateSize(initialSize, totalSize, force = false) {
        this._headerSize = this.hasHeader ? this.asPercent(this.component.headerSize, totalSize) : 0;
        this._minSize = this.component.minSize ? this.asPercent(this.component.minSize, totalSize) : 0;
        this._size =
            this.scale === 'none' && this.component.size && !force
                ? this.asPercent(this.component.size, totalSize)
                : initialSize;
    }
    get collapseToMinSize() {
        return this.component.collapseToMinSize;
    }
    get style() {
        return {
            order: (this._index * 2).toString(),
            flex: this.expanded ? `1 1 ${this.size}%` : COLLAPSED_SIZE
        };
    }
    asPercent(size, totalSize) {
        return size / (totalSize * 0.01);
    }
}
class SiSplitComponent {
    initialSizes = [];
    _dragging = false;
    _mixedSize = false;
    _unsubscribe = new Subject();
    _zone = inject(NgZone);
    cdRef = inject(ChangeDetectorRef);
    uiStateService = inject(SiUIStateService, { optional: true });
    splitPartComponents;
    orientation = 'horizontal';
    sizes = [];
    sizesChange = new EventEmitter();
    gutterSize = 16;
    /**
     * An optional stateId to uniquely identify a component instance.
     * Required for persistence of ui state.
     */
    stateId;
    splitContainer;
    gutters;
    splitParts = [];
    trackByIndex = (index) => index;
    ngAfterViewInit() {
        this.splitPartComponents.changes
            .pipe(startWith(undefined), delay(0), tap(() => this._unsubscribe.next()), switchMap(() => this.uiStateService && this.stateId
            ? this.uiStateService.load(this.stateId).pipe(take(1))
            : of(undefined)))
            .subscribe(state => this.initSplitParts(state));
    }
    ngOnDestroy() {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }
    ngOnChanges(changes) {
        if (changes.orientation && !changes.orientation.firstChange) {
            this.splitParts.forEach(part => part.updateOrientation(this.orientation));
        }
        if (changes.sizes && changes.sizes.currentValue !== changes.sizes.previousValue) {
            // if the previous value is undefined, it is the initial setting of the sizes.
            if (changes.sizes.currentValue && !changes.sizes.previousValue) {
                this.initialSizes = changes.sizes.currentValue;
            }
            // Prevent recursions, we fire changes sizes on resizing, which could end
            // up here again. So do not resize again if it is the same object.
            if (this.sizesChanged(this.sizes, changes.sizes.previousValue)) {
                this.resizeSplitParts();
            }
        }
    }
    sizesChanged(a, b) {
        if (!b || a.length !== b.length) {
            return true;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return true;
            }
        }
        return false;
    }
    onResize() {
        if (this.splitParts.length && !this._dragging) {
            this.splitParts.forEach(part => part.updateSize(part.size, this.containerSize));
            this.updateView();
        }
    }
    showGutter(index) {
        return (index < this.splitParts.length - 1 &&
            this.splitParts[index].expanded &&
            this.splitParts[index + 1].expanded);
    }
    get containerSize() {
        const container = this.splitContainer.nativeElement;
        return this.orientation === 'horizontal' ? container.clientWidth : container.clientHeight;
    }
    get totalGutterSize() {
        const gutters = this.gutters.filter((val, index) => this.showGutter(index));
        return gutters.length !== 0 ? gutters.length * this.gutterSize : 0;
    }
    get unsubscribe$() {
        return this._unsubscribe.asObservable();
    }
    drag(event, status) {
        const position = this.getPosition(event);
        const diff = position - status.start;
        // handle the pixel size of the two split parts as 100%
        const totalPixel = status.startPixel1 + status.startPixel2;
        const totalPercent = status.startPercent1 + status.startPercent2;
        const relPercent1 = this.getAsPercent(status.startPercent1, totalPercent);
        const relPercent2 = 100 - relPercent1;
        let percentDiff = this.getAsPercent(diff, totalPixel);
        if (this.orientation === 'horizontal' && isRTL()) {
            percentDiff *= -1;
        }
        percentDiff = Math.min(percentDiff, relPercent2);
        percentDiff = Math.max(percentDiff, -relPercent1);
        // scale the 100% diff percentage to the total view
        percentDiff = percentDiff * (status.startPercent1 + status.startPercent2) * 0.01;
        this.splitParts[status.index].size = this.ensurePercentRange(status.startPercent1 + percentDiff);
        this.splitParts[status.index + 1].size = this.ensurePercentRange(status.startPercent2 - percentDiff);
        this.ensureMinSplitPartSizesAfterDrag(status.index);
        this.splitParts.forEach(part => {
            if (part.expanded) {
                part.applyStyle();
            }
        });
    }
    ensureMinSplitPartSizesAfterDrag(index) {
        const part1 = this.splitParts[index];
        const part2 = this.splitParts[index + 1];
        if (part1.size < part1.minSize) {
            part2.size += part1.size - part1.minSize;
            part1.size = part1.minSize;
        }
        if (part2.size < part2.minSize) {
            part1.size += part2.size - part2.minSize;
            part2.size = part2.minSize;
        }
    }
    ensurePercentRange(percentage) {
        return Math.max(0, Math.min(100, percentage));
    }
    getAsPercent(size, totalSize) {
        return size / (totalSize * 0.01);
    }
    getPosition(event) {
        if (event instanceof MouseEvent) {
            return this.orientation === 'horizontal' ? event.clientX : event.clientY;
        }
        else if (event instanceof TouchEvent) {
            return this.orientation === 'horizontal'
                ? event.touches[0].clientX
                : event.touches[0].clientY;
        }
        return 0;
    }
    fireResizeEvent() {
        this.saveUIState();
        this.cdRef.markForCheck();
    }
    initSplitParts(state) {
        this.splitParts = this.splitPartComponents.map((component, index) => {
            // load the stored ui state for a split part if
            //  - the part has an id
            //  - the part uses scale mode 'none'
            //  - the initial size has not changed to the initial size that we used at storing
            //    the state. This ensures that we can overwrite the stored sizing when setting
            //    new input sized via the API. Or, the part is collapsed (not expanded).
            if (component.stateId &&
                state?.splitParts &&
                component.scale === 'none' &&
                (this.sizes[index] === state.splitParts[component.stateId].initialSize ||
                    !state.splitParts[component.stateId].expanded)) {
                const splitPartState = state.splitParts[component.stateId];
                this.sizes[index] = splitPartState.size;
                component.expanded = splitPartState.expanded ?? true;
            }
            const splitPart = new SplitPart(component, index);
            splitPart.updateSize(this.sizes[index], this.containerSize);
            this._mixedSize = this._mixedSize || splitPart.scale === 'none';
            return splitPart;
        });
        this.splitParts.forEach(part => {
            part
                .observeStateChange(this.orientation)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => {
                this.updateView(part);
            });
        });
        setTimeout(() => {
            this.updateView();
            this.cdRef.detectChanges();
            this.setupDragEvents();
            this.splitParts
                .filter(part => part.scale === 'none')
                .forEach(part => part.notifySizeChange());
            this.cdRef.detectChanges();
        });
    }
    resizeSplitParts() {
        this.splitParts.forEach((part, index) => {
            part.updateSize(this.sizes[index], this.containerSize, true);
            this._mixedSize = this._mixedSize || part.scale === 'none';
        });
        setTimeout(() => {
            this.updateView();
            this.splitParts
                .filter(part => part.scale === 'none')
                .forEach(part => part.notifySizeChange());
            this.cdRef.markForCheck();
        });
    }
    setupDragEvents() {
        this._zone.runOutsideAngular(() => {
            const mousemove$ = merge(fromEvent(document, 'mousemove'), fromEvent(document, 'touchmove'));
            const mouseup$ = merge(fromEvent(document, 'mouseup'), fromEvent(document, 'touchend'));
            this.gutters.forEach((gutter, index) => {
                const mousedown$ = merge(fromEvent(gutter.nativeElement, 'mousedown'), fromEvent(gutter.nativeElement, 'touchstart'));
                mousedown$
                    .pipe(takeUntil(this.unsubscribe$), switchMap(startEvent => {
                    startEvent.preventDefault();
                    this._dragging = true;
                    const part1 = this.splitParts[index];
                    const part2 = this.splitParts[index + 1];
                    const status = {
                        index,
                        start: this.getPosition(startEvent),
                        startPixel1: part1.pixelSize,
                        startPixel2: part2.pixelSize,
                        startPercent1: part1.size,
                        startPercent2: part2.size
                    };
                    return mousemove$.pipe(throttleTime(5), tap(evt => this.drag(evt, status)), takeUntil(mouseup$.pipe(tap(() => {
                        this.fireResizeEvent();
                        part1.notifySizeChange();
                        part2.notifySizeChange();
                        this._dragging = false;
                        this.cdRef.detectChanges();
                    }))), throttleTime(200));
                }))
                    .subscribe(() => this.fireResizeEvent());
            });
        });
    }
    updateAutoSize(activePart) {
        let factor;
        if (activePart?.expanded) {
            factor = (100 - activePart.size) / 100;
            this.splitParts
                .filter((part, index) => part.expanded && activePart.index !== index)
                .forEach(part => (part.size *= factor));
        }
        let totalSize = 0;
        this.splitParts.forEach((part, index) => {
            if (activePart && !activePart.expanded) {
                if (activePart.direction === 'start' && index < activePart.index) {
                    part.collapsed();
                }
                if (activePart.direction === 'end' && index > activePart.index) {
                    part.collapsed();
                }
            }
            totalSize += part.actualSize;
        });
        // recalculate size to fill 100%
        factor = totalSize !== 0 ? 100 / totalSize : 0;
        this.splitParts.filter(part => part.expanded).forEach(part => (part.size *= factor));
    }
    updateMixedSize() {
        let autoSize = 100;
        let actualSize = 0;
        this.splitParts.forEach(part => {
            if (part.scale === 'none') {
                autoSize -= part.actualSize;
            }
            else {
                actualSize += part.size;
            }
        });
        autoSize -= this.getAsPercent(this.totalGutterSize, this.containerSize);
        const autoSizeFactor = actualSize ? autoSize / actualSize : 1;
        this.splitParts.forEach(part => {
            if (part.scale === 'auto') {
                part.size *= autoSizeFactor;
            }
        });
    }
    updateView(activePart) {
        if (this._mixedSize) {
            this.updateMixedSize();
        }
        else {
            this.updateAutoSize(activePart);
        }
        this.splitParts.forEach(part => part.applyStyle());
        if (activePart) {
            setTimeout(() => this.fireResizeEvent());
        }
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    saveUIState() {
        const state = { contextId: this.stateId, splitParts: {} };
        const newSizes = [];
        this.splitParts.forEach((part, index) => {
            newSizes.push(part.size);
            if (part.stateId) {
                state.splitParts[part.stateId] = {
                    size: part.size,
                    expanded: part.expanded,
                    initialSize: this.initialSizes[index]
                };
            }
        });
        this.sizes = newSizes;
        this.sizesChange.emit(this.sizes);
        if (this.stateId && this.uiStateService) {
            this.uiStateService.save(state);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSplitComponent, selector: "si-split", inputs: { orientation: "orientation", sizes: "sizes", gutterSize: "gutterSize", stateId: "stateId" }, outputs: { sizesChange: "sizesChange" }, host: { listeners: { "window:resize": "onResize()" }, classAttribute: "si-split" }, queries: [{ propertyName: "splitPartComponents", predicate: SiSplitPartComponent }], viewQueries: [{ propertyName: "splitContainer", first: true, predicate: ["splitContainer"], descendants: true, static: true }, { propertyName: "gutters", predicate: ["gutter"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div #splitContainer class=\"si-split-container\" [ngClass]=\"orientation\">\n  <ng-content></ng-content>\n\n  <ng-container *ngFor=\"let splitPart of splitParts; trackBy: trackByIndex; let i = index\">\n    <div\n      *ngIf=\"i < splitParts.length - 1\"\n      #gutter\n      class=\"si-split-gutter\"\n      [class.d-none]=\"!showGutter(i)\"\n      [style.order]=\"i * 2 + 1\"\n      [style.width.px]=\"orientation === 'horizontal' ? gutterSize : null\"\n      [style.height.px]=\"orientation === 'vertical' ? gutterSize : null\"\n    ></div>\n  </ng-container>\n</div>\n", styles: [":host{--si-split-gutter-border-thickness: 0;--si-split-gutter-border-style: solid;--si-split-gutter-handle-size: 6px;--si-split-gutter-handle-length: 40px;--si-split-part-header-height: 40px;--si-split-part-header-background: var(--element-base-0);--si-split-part-header-padding: 6px;--si-split-part-header-collapsed-horizontal-padding: 6px;--si-split-part-header-status-border-width: 4px;--si-split-part-header-status-container-padding: 0;--si-split-part-header-status-icon-color: var(--si-split-part-title-color, initial);--si-split-part-header-status-icon-size: 20px;--si-split-part-title-color: var(--element-text-primary);--si-split-part-title-font-size: .875rem;--si-split-part-title-font-weight: bold;--si-split-part-title-line-height: 1.143;--si-split-part-action-margin: 8px;--si-split-part-flex-basis: 50px;--si-split-button-color: var(--element-text-primary);--si-split-button-font-size: 20px;--si-split-button-line-height: 1;--si-split-button-text-overflow: ellipsis;--si-split-button-padding: 4px;--si-split-button-shade-background-hover: rgba(47, 67, 99, .4);display:flex;inline-size:100%}.si-split-container{flex:1 1 0;display:flex;overflow:hidden;inline-size:100%;flex-direction:row}.si-split-container.vertical{flex-direction:column}.si-split-gutter{flex-shrink:0;flex-grow:0;background:var(--element-base-0);cursor:ew-resize}.si-split-gutter:before{display:block;content:\"\";position:relative;inset-block-start:50%;margin-block-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inset-inline-start:50%;margin-inline-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);inline-size:var(--si-split-gutter-handle-size);block-size:var(--si-split-gutter-handle-length);background:var(--element-ui-2);border-radius:3px}.vertical .si-split-gutter{inline-size:auto;cursor:ns-resize}.vertical .si-split-gutter:before{margin-block-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);margin-inline-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inline-size:var(--si-split-gutter-handle-length);block-size:var(--si-split-gutter-handle-size)}.si-split-gutter:hover:before{background:var(--element-ui-0-hover)}.si-split-gutter:active:before{background:var(--element-ui-0)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
__decorate([
    WebComponentContentChildren(SiSplitPartComponent)
], SiSplitComponent.prototype, "splitPartComponents", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-split', host: { class: 'si-split' }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #splitContainer class=\"si-split-container\" [ngClass]=\"orientation\">\n  <ng-content></ng-content>\n\n  <ng-container *ngFor=\"let splitPart of splitParts; trackBy: trackByIndex; let i = index\">\n    <div\n      *ngIf=\"i < splitParts.length - 1\"\n      #gutter\n      class=\"si-split-gutter\"\n      [class.d-none]=\"!showGutter(i)\"\n      [style.order]=\"i * 2 + 1\"\n      [style.width.px]=\"orientation === 'horizontal' ? gutterSize : null\"\n      [style.height.px]=\"orientation === 'vertical' ? gutterSize : null\"\n    ></div>\n  </ng-container>\n</div>\n", styles: [":host{--si-split-gutter-border-thickness: 0;--si-split-gutter-border-style: solid;--si-split-gutter-handle-size: 6px;--si-split-gutter-handle-length: 40px;--si-split-part-header-height: 40px;--si-split-part-header-background: var(--element-base-0);--si-split-part-header-padding: 6px;--si-split-part-header-collapsed-horizontal-padding: 6px;--si-split-part-header-status-border-width: 4px;--si-split-part-header-status-container-padding: 0;--si-split-part-header-status-icon-color: var(--si-split-part-title-color, initial);--si-split-part-header-status-icon-size: 20px;--si-split-part-title-color: var(--element-text-primary);--si-split-part-title-font-size: .875rem;--si-split-part-title-font-weight: bold;--si-split-part-title-line-height: 1.143;--si-split-part-action-margin: 8px;--si-split-part-flex-basis: 50px;--si-split-button-color: var(--element-text-primary);--si-split-button-font-size: 20px;--si-split-button-line-height: 1;--si-split-button-text-overflow: ellipsis;--si-split-button-padding: 4px;--si-split-button-shade-background-hover: rgba(47, 67, 99, .4);display:flex;inline-size:100%}.si-split-container{flex:1 1 0;display:flex;overflow:hidden;inline-size:100%;flex-direction:row}.si-split-container.vertical{flex-direction:column}.si-split-gutter{flex-shrink:0;flex-grow:0;background:var(--element-base-0);cursor:ew-resize}.si-split-gutter:before{display:block;content:\"\";position:relative;inset-block-start:50%;margin-block-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inset-inline-start:50%;margin-inline-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);inline-size:var(--si-split-gutter-handle-size);block-size:var(--si-split-gutter-handle-length);background:var(--element-ui-2);border-radius:3px}.vertical .si-split-gutter{inline-size:auto;cursor:ns-resize}.vertical .si-split-gutter:before{margin-block-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);margin-inline-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inline-size:var(--si-split-gutter-handle-length);block-size:var(--si-split-gutter-handle-size)}.si-split-gutter:hover:before{background:var(--element-ui-0-hover)}.si-split-gutter:active:before{background:var(--element-ui-0)}\n"] }]
        }], propDecorators: { splitPartComponents: [{
                type: ContentChildren,
                args: [SiSplitPartComponent]
            }], orientation: [{
                type: Input
            }], sizes: [{
                type: Input
            }], sizesChange: [{
                type: Output
            }], gutterSize: [{
                type: Input
            }], stateId: [{
                type: Input
            }], splitContainer: [{
                type: ViewChild,
                args: ['splitContainer', { static: true }]
            }], gutters: [{
                type: ViewChildren,
                args: ['gutter']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class SiSplitModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiSplitModule, declarations: [SiSplitComponent, SiSplitPartComponent], imports: [CommonModule, SiTranslateModule], exports: [SiSplitComponent, SiSplitPartComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitModule, imports: [CommonModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSplitModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiSplitComponent, SiSplitPartComponent],
                    exports: [SiSplitComponent, SiSplitPartComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiSplitComponent, SiSplitModule, SiSplitPartComponent };
